import { TransformedDeliverySpeedOptionOfferInfo } from "@swiggy-private/cart-types";
import { replaceText, useEffectOnce } from "@swiggy-private/core-ui";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { DeliverySpeedOfferInfoContainer } from "./styles";
import { AnalyticsContext, AnalyticsContextValue } from "@swiggy-private/checkout-core";
import { DELIVERY_SPEED_WIDGET_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { OFFER_EXPIRY_PLACEHOLDER } from "./constant";
import { isEmpty } from "@swiggy-private/js-utils";
import { formatTimeLeft } from "@cart-ui/helpers/DeliverySpeed";

export interface DeliverySpeedOfferInfoProps {
    offerInfo: TransformedDeliverySpeedOptionOfferInfo;
    onTimeEnd?: VoidFunction;
    impressionDeliveryTypeOffer?: (
        impressionEvent: AnalyticsContextValue["impressionEvent"],
        offerMessage?: string,
        offerExpiry?: number,
    ) => void;
    impressionDeliveryTypeOfferExpired?: (impressionEvent: AnalyticsContextValue["impressionEvent"]) => void;
}

const DeliverySpeedOfferInfo: React.FC<DeliverySpeedOfferInfoProps> = ({
    offerInfo,
    onTimeEnd,
    impressionDeliveryTypeOffer,
    impressionDeliveryTypeOfferExpired,
}) => {
    const [timeLeft, setTimeLeft] = useState(offerInfo?.timeLeftInSeconds || 0);
    const timerInterval = useRef<NodeJS.Timeout | undefined | number>();

    const { impressionEvent } = useContext(AnalyticsContext);

    const offerIsBeyondTimerWindow = useMemo(() => {
        return offerInfo && (offerInfo.timeLeftInSeconds ?? 0) <= 0;
    }, [offerInfo]);

    useEffect(() => {
        if (offerInfo && (offerInfo.timeLeftInSeconds ?? 0) > 0 && timeLeft <= 0) {
            onTimeEnd?.();
            impressionDeliveryTypeOfferExpired?.(impressionEvent);
            return;
        }

        if (!offerIsBeyondTimerWindow) {
            timerInterval.current = setInterval(() => {
                setTimeLeft(prevTime => prevTime - 1);
            }, 1000);
        }

        return () => {
            if (timerInterval.current != null) {
                clearInterval(timerInterval.current as NodeJS.Timeout);
            }
        };
    }, [timeLeft, onTimeEnd, offerInfo, impressionDeliveryTypeOfferExpired, impressionEvent, offerIsBeyondTimerWindow]);

    useEffectOnce(() => {
        impressionDeliveryTypeOffer?.(impressionEvent, offerInfo?.offerMessage, offerInfo?.timeLeftInSeconds);
    });

    // Split the message into static parts that won't change with timer
    const { messageParts, hasTimer } = useMemo(() => {
        if (!offerInfo?.offerMessage) {
            return { messageParts: [], hasTimer: false };
        }
        const parts = offerInfo.offerMessage.split(OFFER_EXPIRY_PLACEHOLDER);
        return {
            messageParts: parts.map(part => replaceText(part)),
            hasTimer: parts.length > 1,
        };
    }, [offerInfo?.offerMessage]);

    // Format time separately
    const formattedTime = useMemo(() => formatTimeLeft(timeLeft), [timeLeft]);

    if (isEmpty(offerInfo?.offerMessage)) {
        return null;
    }

    return (
        <DeliverySpeedOfferInfoContainer
            variant="V2.body3.neutral.medium"
            textColor={offerInfo?.color}
            gradient={offerInfo?.gradient}
            data-testid={DELIVERY_SPEED_WIDGET_TEST_IDS.DELIVERY_SPEED_ITEM_OFFERS}
        >
            {hasTimer ? (
                <div>
                    {messageParts[0]}
                    <span>{formattedTime}</span>
                    {messageParts[1]}
                </div>
            ) : (
                messageParts[0]
            )}
        </DeliverySpeedOfferInfoContainer>
    );
};

export default React.memo(DeliverySpeedOfferInfo);
